import * as React from 'react'
import {useCallback, useRef, useState} from 'react'
import {Lang} from 'yougile-lang'
import {DataObjectId, Hub} from 'yougile-model'
import {SharedBoardsClient} from '@client/shared-boards-client'
import {TaskTemplateClient} from '@client/task-template-client'
import {Tracking, TrackingEvent} from '@common/tracking'
import {useAddTaskStore} from '@components/add-task/add-task-store'
import {boardSettingsStore} from '@components/board/board-settings/board-settings-store'
import {IconButtonNew} from '@components/common/base/buttons'
import {IconProps} from '@components/common/base/buttons/types'
import {Menu, MenuSimpleItemData} from '@components/common/base/menu'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {LinkButtonWrapped} from '@components/movement-templates/link-button-wrapped'
import {cn} from '@containers/html-utils'

interface Props {
  boardId: DataObjectId
}

export const TaskTemplatesMenu = (props: Props) => {
  const {boardId} = props
  const list = TaskTemplateClient.listTemplatesInBoardEnabled(boardId)

  const [isMenuOpen, setIsMenuOpen] = useState(false)

  const openMenu = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setIsMenuOpen(true)
    },
    [setIsMenuOpen]
  )

  const closeMenu = useCallback(() => setIsMenuOpen(false), [setIsMenuOpen])

  const ref = useRef(null)

  if (!TaskTemplateClient.hasFeature()) {
    return null
  }

  if (SharedBoardsClient.isGuest()) {
    return null
  }

  return (
    <>
      <MenuIcon ref={ref} isMenuOpen={isMenuOpen} openMenu={openMenu} />

      <MenuDropdown
        {...props}
        isMenuOpen={isMenuOpen}
        closeMenu={closeMenu}
        list={list}
        reference={ref}
      />
    </>
  )
}

interface MovementDropdownProps {
  isMenuOpen: boolean
  closeMenu: () => void
  list: Hub[]
  reference: React.RefObject<Element> | Element | null
}

const MenuDropdownHeader = ({closeMenu}: {closeMenu: () => void}) => {
  return (
    <div className="h-56 px-24 flex items-center gap-16">
      <span className="cursor-default text-sm-semibold text-primary">
        {Lang.msg.taskTemplates.title}
      </span>

      <LinkButtonWrapped
        icon={'IconPlus'}
        text={Lang.msg.taskTemplates.createTemplate}
        className="ml-auto"
        onClick={() => {
          closeMenu()
          boardSettingsStore.setCurrentTabId('taskTemplates', 'create')
          boardSettingsStore.toggle()
          void Tracking.send(
            TrackingEvent.boardSettingsTaskTemplatesCreateViaColumnMenu
          )
        }}
      />

      <LinkButtonWrapped
        icon={'IconCustomSettings'}
        text={Lang.msg.taskTemplates.settingsTemplate}
        onClick={() => {
          closeMenu()
          boardSettingsStore.setCurrentTabId('taskTemplates', 'list')
          boardSettingsStore.toggle()
          void Tracking.send(
            TrackingEvent.boardSettingsTaskTemplatesListViaColumnMenu
          )
        }}
      />
    </div>
  )
}

const MenuDropdown = (props: Props & MovementDropdownProps) => {
  const {isMenuOpen, closeMenu, list, reference} = props
  const [, store] = useAddTaskStore()

  if (!isMenuOpen) {
    return null
  }

  const menuItems: MenuSimpleItemData[] = []
  menuItems.push(
    ...list.map(template => ({
      id: `task-template--task-card-${template.id}`,
      icon: 'IconCustomTemplate' as const,
      title: template.data.taskTemplate.title,
      popupMenu: true,
      onClick: () => {
        store.showWithTemplateId(template.id)
      }
    }))
  )

  return (
    <Dropdown
      reference={reference}
      hideArrow
      theme="new-menu"
      content={
        <>
          <div
            className={
              'min-w-400 max-w-400 bg-background-primary rounded-8 shadow-lg'
            }
          >
            <MenuDropdownHeader closeMenu={closeMenu} />
            {menuItems.length ? (
              <Menu
                items={menuItems}
                onItemClicked={closeMenu}
                className={'min-w-400 max-w-400'}
                popupMenu
              />
            ) : (
              <MenuDropdownEmpty />
            )}
          </div>
        </>
      }
      visible
      onVisibleChange={closeMenu}
    />
  )
}

export const MenuDropdownEmpty = () => {
  return (
    <div className="px-24 pt-16 pb-24 cursor-default text-primary">
      <div>{Lang.msg.taskTemplates.emptyText1}</div>
      <div className="mt-8">{Lang.msg.taskTemplates.emptyText2}</div>
    </div>
  )
}

interface MovementMenuIconProps {
  isMenuOpen: boolean
  openMenu: (e: React.MouseEvent) => void
}

const MenuIcon = React.forwardRef((props: MovementMenuIconProps, ref) => {
  const {openMenu} = props

  const iconProps: IconProps = {
    icon: 'IconCustomTemplate', // IconDotsVertical
    color: 'secondary' as const,
    size: 'small',
    wrapperSize: 'big',
    className: cn('relative', '!w-24', '!h-24', '-my-4')
  }

  return <IconButtonNew ref={ref} {...iconProps} onClick={openMenu} />
})
